'use client';

import { App, ConfigProvider, theme } from 'antd';
import { ThemeProvider } from 'antd-style';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import AntdStaticMethods from '@/components/antd-static-methods';
// openui (generative UI) has its OWN theme, applied via --openui-* CSS vars whose
// dark override keys on the OS `prefers-color-scheme` — NOT on the app theme. With
// no openui ThemeProvider mounted, cards followed the OS and rendered dark inside
// our (always-light) app. Mounting one here injects the light vars on `body`,
// overriding that media query so generative UI tracks the app theme.
import { ThemeProvider as OpenUIThemeProvider } from '@/components/generative/built-in/src/components/ThemeProvider';

import { useGlobalStore } from '@/store/global';
import { systemStatusSelectors } from '@/store/global/selectors';
import { GlobalStyle } from '@/styles';

// Keep generative-UI (openui) fonts identical to the rest of the app. The app
// loads Inter / Geist Mono via next/font (exposed as --font-inter / --font-geist-mono);
// openui's defaults use the bare "Inter" family which does NOT resolve to that
// loaded face, so cards rendered in a different (system) font. Point openui's font
// tokens at the same CSS variables. Passed as `lightTheme` (applies to both modes).
const OPENUI_SANS =
  "var(--font-inter, 'Inter'), ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

// Recolour generative-UI charts from openui's default blue ("ocean") to the app's
// purple. Charts read `theme.defaultChartPalette` before falling back to their
// built-in palette, and getDistributedColors picks the MIDDLE shade for a single
// series — so index 5 is #713D98, the app's antd `colorPrimary`. It's a dark→light
// ramp (colorLinkActive #5C2E80 → colorPrimary #713D98 → colorLinkHover #8F5BB3 →
// lavender), so multi-series charts read as an on-brand purple gradient.
const OPENUI_CHART_PURPLE = [
  '#2D1840',
  '#3C2054',
  '#4A2866',
  '#5C2E80', // colorLinkActive
  '#663895',
  '#713D98', // colorPrimary — single-series charts land here
  '#8F5BB3', // colorLinkHover
  '#A87CC7',
  '#C29EDB',
  '#DBC2EC',
  '#F0E4F9',
];

// Keep generative-UI (openui) fonts identical to the rest of the app. The app
// loads Inter / Geist Mono via next/font (exposed as --font-inter / --font-geist-mono);
// openui's defaults use the bare "Inter" family which does NOT resolve to that
// loaded face, so cards rendered in a different (system) font. Point openui's font
// tokens at the same CSS variables. Passed as `lightTheme` (applies to both modes).
const OPENUI_THEME_OVERRIDES = {
  fontBody: OPENUI_SANS,
  fontHeading: OPENUI_SANS,
  fontLabel: OPENUI_SANS,
  fontNumbers: OPENUI_SANS,
  fontCode:
    "var(--font-geist-mono, 'Geist Mono'), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  defaultChartPalette: OPENUI_CHART_PURPLE,
  // openui's "info" tone defaults to blue (e.g. a TextCallout variant="info", the
  // note above an org chart). This app has no blue in its palette — its brand tone
  // IS the purple primary — so recolour the whole info family to match the app
  // (#713D98 = colorPrimary, #8F5BB3 = colorLinkHover). Warning/success/danger keep
  // their semantic colours.
  textInfoPrimary: '#713D98',
  textInfoInverted: '#F0E4F9',
  borderInfoEmphasis: '#713D98',
  borderInfo: 'rgba(113, 61, 152, 0.08)',
  infoBackground: 'rgba(113, 61, 152, 0.12)',
};

interface GlobalThemeProviderProps extends PropsWithChildren {}

const GlobalThemeProvider = ({ children }: GlobalThemeProviderProps) => {
  const themeMode = useGlobalStore(systemStatusSelectors.themeMode);
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');

  // Determine the actual theme based on themeMode
  // TEMPORARILY DISABLED: Always use light mode
  useEffect(() => {
    // Force light mode - remove this block to restore dark mode functionality
    setCurrentTheme('light');
    return;

    // Original dark mode logic (commented out temporarily)
    /*
    let actualTheme: 'light' | 'dark' = 'light';

    if (themeMode === 'auto') {
      // Use system preference
      if (typeof window !== 'undefined') {
        actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

        // Listen for system theme changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
          setCurrentTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
    } else {
      actualTheme = themeMode as 'light' | 'dark';
    }

    setCurrentTheme(actualTheme);
    */
  }, [themeMode]);

  // Update document attributes for theme
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', currentTheme);
      // Preserve next/font's generated classes. Replacing the full className removes
      // --font-inter/--font-geist-mono after hydration and causes a visible font swap.
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(currentTheme);
    }
  }, [currentTheme]);

  // Antd theme configuration
  const antdTheme = useMemo(() => {
    const tooltipBg =
      typeof window !== 'undefined'
        ? getComputedStyle(document.documentElement).getPropertyValue('--tooltip-background').trim()
        : '#6824A6';

    return {
      algorithm: currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        // Prefer the next/font Inter (--font-inter) when loaded; VALID var()
        // fallback to the app's system stack otherwise. NOTE: on this deployment
        // --font-inter is currently empty (next/font Inter isn't loading), so this
        // resolves to ui-sans-serif — matching the generative-UI cards. Both track
        // the same stack, so they stay in sync regardless of the webfont.
        fontFamily:
          "var(--font-inter, 'Inter'), ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
        fontFamilyCode:
          "var(--font-geist-mono), 'Geist Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        colorPrimary: '#713D98',
        // Links to the theme primary purple. lobehub's Markdown renders <a> with
        // colorInfoText / colorInfoHover (NOT colorLink), so both families are set
        // to keep every link — chat messages and native antd links — on-theme.
        colorLink: '#713D98',
        colorLinkHover: '#8F5BB3',
        colorLinkActive: '#5C2E80',
        colorInfoText: '#713D98',
        colorInfoHover: '#8F5BB3',
        borderRadius: 6,
      },
      components: {
        Tooltip: {
          colorBgSpotlight: tooltipBg || '#6824A6',
        },
      },
    };
  }, [currentTheme]);

  return (
    <ConfigProvider theme={antdTheme}>
      <App>
        <AntdStaticMethods />
        <ThemeProvider appearance={currentTheme} themeMode={currentTheme}>
          <GlobalStyle />
          <OpenUIThemeProvider lightTheme={OPENUI_THEME_OVERRIDES} mode={currentTheme}>
            {children}
          </OpenUIThemeProvider>
        </ThemeProvider>
      </App>
    </ConfigProvider>
  );
};

export default GlobalThemeProvider;
